import { useEffect, useMemo } from 'react';
import { useAuth } from '../auth';
import { useAdAccountStore } from '../stores/adAccountStore';
import { useMetaMessagingStore, type MetaConversation } from '../stores/metaMessagingStore';
import { usePermissionsStore } from '../stores/permissionsStore';

export type MetaChannel = 'instagram' | 'messenger';

export type MetaUnavailableReason = 'not_connected' | 'messaging_off' | 'no_permission' | 'no_pages';

export interface MetaChannelsState {
  /** L'org ha la messaggistica Meta attiva e l'utente può vederla (permesso + almeno una pagina, o superadmin). */
  available: boolean;
  /** Perché non è disponibile (null se lo è): guida lo stato "non collegato" nel drawer. */
  reason: MetaUnavailableReason | null;
  /** Dati di configurazione ancora in caricamento: meglio uno spinner che uno stato sbagliato. */
  loading: boolean;
  /** L'utente può rispondere (permesso send, o superadmin). */
  canSend: boolean;
  /** L'utente può assegnare le pagine agli altri (permesso manage, o superadmin). */
  canManage: boolean;
  isSuperAdmin: boolean;
  /** Conversazioni in attesa di risposta, per canale (badge sul selettore). */
  pendingByChannel: Record<MetaChannel, number>;
  conversationsByChannel: Record<MetaChannel, MetaConversation[]>;
}

/**
 * Stato dei canali Meta (Instagram, Messenger) per l'utente corrente.
 *
 * Regole (decisione Luca 02/09/2026): il selettore di canale nel drawer compare
 * solo se l'org ha `messaging_enabled` e l'utente ha il permesso di vista e
 * almeno una pagina assegnata in `meta_page_user_access`. Il superadmin vede
 * tutto. La visibilità vera delle conversazioni resta della RLS: qui si decide
 * solo cosa mostrare.
 */
export function useMetaChannels(): MetaChannelsState {
  const { userProfile } = useAuth();
  const isSuperAdmin = userProfile?.is_super_admin === true;
  const metaConfig = useAdAccountStore((s) => s.metaConfig);
  const fetchAdConfigs = useAdAccountStore((s) => s.fetchConfigs);
  const adConfigsLoading = useAdAccountStore((s) => s.loading);
  const pageAccess = useMetaMessagingStore((s) => s.pageAccess);
  const conversations = useMetaMessagingStore((s) => s.conversations);
  const reads = useMetaMessagingStore((s) => s.reads);
  const isUnread = useMetaMessagingStore((s) => s.isUnread);
  const fetchPageAccess = useMetaMessagingStore((s) => s.fetchPageAccess);
  const hasPermission = usePermissionsStore((s) => s.hasPermission);

  useEffect(() => {
    if (!userProfile?.organization_id) return;
    if (!metaConfig) fetchAdConfigs().catch(() => undefined);
    if (pageAccess === null) fetchPageAccess().catch(() => undefined);
  }, [userProfile?.organization_id, metaConfig, pageAccess, fetchAdConfigs, fetchPageAccess]);

  const messagingEnabled = Boolean((metaConfig?.settings as { messaging_enabled?: boolean } | undefined)?.messaging_enabled);
  const canView = isSuperAdmin || hasPermission('meta_messaging', 'view');
  const canSend = isSuperAdmin || hasPermission('meta_messaging', 'send');
  const canManage = isSuperAdmin || hasPermission('meta_messaging', 'manage');
  const hasPages = isSuperAdmin || (pageAccess?.length ?? 0) > 0;
  const connected = Boolean(metaConfig?.is_connected);
  const available = connected && messagingEnabled && canView && hasPages;
  const reason: MetaUnavailableReason | null = available
    ? null
    : !connected
      ? 'not_connected'
      : !messagingEnabled
        ? 'messaging_off'
        : !canView
          ? 'no_permission'
          : 'no_pages';
  // metaConfig resta null anche DOPO il fetch se l'org non ha mai collegato Meta:
  // il caricamento va letto dal flag dello store, non dall'assenza della config.
  const loading = Boolean(userProfile?.organization_id) && (adConfigsLoading || pageAccess === null);

  const conversationsByChannel = useMemo(() => {
    const byChannel: Record<MetaChannel, MetaConversation[]> = { instagram: [], messenger: [] };
    for (const c of conversations) {
      if (c.channel === 'instagram' || c.channel === 'messenger') byChannel[c.channel].push(c);
    }
    return byChannel;
  }, [conversations]);

  const pendingByChannel = useMemo(
    () => ({
      instagram: conversationsByChannel.instagram.filter((c) => isUnread(c)).length,
      messenger: conversationsByChannel.messenger.filter((c) => isUnread(c)).length,
    }),
    // `reads` e' la dipendenza vera di isUnread (legge lo store).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversationsByChannel, reads]
  );

  return { available, reason, loading, canSend, canManage, isSuperAdmin, pendingByChannel, conversationsByChannel };
}
